import { PANEL_GROUPS } from "./constants";
import { selectPanelGroups } from "./selectors";

export const buildPanel = (panel, persistedSizes) => ({
  size: persistedSizes[panel.panelId] ?? panel.defaultSize,
  defaultSize: panel.defaultSize,
  collapsedSize: panel.collapsedSize,
});

export const buildInitialGroups = () =>
  Object.fromEntries(
    Object.entries(PANEL_GROUPS).map(([groupId, { persist, panels }]) => {
      const persistedSizes = readPersistedPanelSizes(groupId, persist);
      const groupPanels = Object.fromEntries(
        panels.map((panel) => [
          panel.panelId,
          buildPanel(panel, persistedSizes),
        ])
      );
      return [groupId, { persist, panels: groupPanels }];
    })
  );

export const setSizes = (state, groupId, sizes) => {
  const group = state.groups[groupId];
  if (!group) {
    return;
  }

  Object.entries(sizes).forEach(([panelId, size]) => {
    if (group.panels[panelId]) {
      group.panels[panelId].size = size;
    }
  });
};

const sizesByKey = (group, key) => {
  const sizes = {};

  Object.entries(group.panels).forEach(([panelId, panel]) => {
    if (panel[key] !== undefined) {
      sizes[panelId] = panel[key];
    }
  });

  return sizes;
};

export const collapsibleSizesFor = (group) =>
  sizesByKey(group, "collapsedSize");

export const expandedSizesFor = (group) => sizesByKey(group, "defaultSize");

export const isPanelCollapsed = (panel) =>
  Boolean(panel) && panel.size === (panel.collapsedSize ?? 0);

const storageKeyFor = (groupId) => `panels:${groupId}`;

export const attachPanelsPersistence = (store) => {
  let lastGroups = null;
  const lastPanelsByGroup = {};

  store.subscribe(() => {
    const groups = selectPanelGroups(store.getState());

    if (groups === lastGroups) {
      return;
    }

    lastGroups = groups;

    Object.entries(groups).forEach(([groupId, group]) => {
      if (!group.persist || group.panels === lastPanelsByGroup[groupId]) {
        return;
      }

      lastPanelsByGroup[groupId] = group.panels;
      const sizes = Object.fromEntries(
        Object.entries(group.panels).map(([panelId, panel]) => [
          panelId,
          panel.size,
        ])
      );
      localStorage.setItem(storageKeyFor(groupId), JSON.stringify(sizes));
    });
  });
};

export const readPersistedPanelSizes = (groupId, persist) => {
  if (!persist) {
    return {};
  }

  try {
    const raw = localStorage.getItem(storageKeyFor(groupId));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};
